import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { searchComplexAreaV4, updateComplexBookmarkV3, updateComplexLandmarkV3 } from '../../../repository/RealEstateRepository';
import { getEduMarker } from "../organism/EducationMapRenderer";
import { getEnvMarker } from "../organism/EnvironmentMapRenderer";
import { ComplexMoveControl, RealEstateMap, renderComplexArea } from "./index";

const getCurrentIcon = () => ({
  content: [
    `<div style="position:relative;width:19px;height:19px;background:#fff;border-radius:20px;border:1px solid #ccc;margin-left:-10px;">`,
    `<div style="position:absolute;width:13px;height:13px;left:2px;top:2px;background:#f00;border-radius:20px;"></div>`,
    `</div>`].join('')
});

const ComplexAreaMap = (props) => {
  const [selectedComplex, setSelectedComplex] = useState(null);
  const [circumstanceMarkers, setCircumstanceMarkers] = useState([]);
  const [currentMarker, setCurrentMarker] = useState(null);

  const [markers, setMarkers] = useState([]);
  const [customOverlays, setCustomOverlays] = useState([]);

  useEffect(() => {
    navigator.geolocation && navigator.geolocation.watchPosition(
      (pos) => {
        setCurrentMarker({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          icon: getCurrentIcon()
        });
      },
      () => { },
      {
        enableHighAccuracy: false,
        maximumAge: 10 * 1000,
        timeout: 5000
      });
  }, []);

  useEffect(() => {
    if (!props.educations || !props.environments) return;
    const _edu = props.educations;
    const _env = props.environments;
    const circumstances = [_edu.middleSchools, _edu.elementarySchools, _edu.academyStreets, _env].flatMap(v => v);
    const circumstanceMarkers = circumstances.map(v => ({
      ...v,
      icon: getEnvMarker(v) || getEduMarker(v)
    }));
    setCircumstanceMarkers(circumstanceMarkers);
  }, [props.educations, props.environments]);

  useEffect(() => {
    const _markers = [].concat(circumstanceMarkers);
    if (currentMarker != null) _markers.push(currentMarker);
    setMarkers(_markers);
  }, [currentMarker, circumstanceMarkers]);

  useEffect(() => {
    const overlays = (props.complexList || [])
      .map(v => {
        v.complex.selected = (selectedComplex && selectedComplex.detail.complex.id === v.complex.id);
        return v;
      })
      .map(v => renderComplexArea(v.complex, v.complexAreaPriceCounts, (el) => {
        setSelectedComplex({ detail: v, element: el });
      }));

    setCustomOverlays(overlays);
    // eslint-disable-next-line
  }, [props.complexList]);

  const getColorByWeight = (weight = 0) => {
    if (weight >= 7500) {
      return "red";
    } else if (weight >= 5000) {
      return "orange"
    } else if (weight >= 4000) {
      return "#e7b439";
    } else if (weight >= 3500) {
      return "green";
    } else if (weight >= 3000) {
      return "teal";
    } else if (weight >= 2500) {
      return "blue";
    } else {
      return "gray";
    }
  }

  const viewFrontYardId = props.viewLivingArea && props.viewLivingArea.frontYardId;
  const polygons = props.bgLivingAreaList.filter(v => v.boundary != null)
    .map(v => ({
      name: v.name,
      boundary: v.boundary.map(c => ({ x: c[0], y: c[1] })),
      option: {
        strokeOpacity: (viewFrontYardId && viewFrontYardId === v.frontYardId) ? 0.8 : 0.2,
        strokeWeight: (viewFrontYardId && viewFrontYardId === v.frontYardId) ? 4 : 2,
        strokeColor: getColorByWeight(v.weight),
        fillOpacity: (v.selected) ? 0.2 : 0.1,
        fillColor: getColorByWeight(v.weight)
      }
    }));

  return (<>
    <RealEstateMap
      onInit={props.onChangeMapBounds}
      onIdle={props.onChangeMapBounds}
      customOverlays={customOverlays}
      polygons={polygons}
      markers={markers} />

    <Cursor size={4} />

    {selectedComplex !== null && (<>
      <div style={{ position: "fixed", top: 108 + 4, left: 4 }}>
        <ComplexAreaMapComplexInfo
          selectedComplex={selectedComplex}
          onChangeSelectedComplex={(c) => {
            setSelectedComplex(c);
            props.reloadComplexArea();
          }} />
      </div>
      <div style={{ position: "fixed", left: 0, bottom: 0, right: 0 }}>
        <ComplexMoveControl element={selectedComplex.element} />
      </div>
    </>
    )}
  </>);
};

const Cursor = (props) => {
  const { size } = props;
  return (<div
    style={{
      position: "fixed", background: "red",
      left: "50%", top: "50%", width: size, height: size,
      marginLeft: (size / 2 * -1), marginTop: (size / 2 * -1),
      borderRadius: size
    }}></div>);
}

const ComplexAreaMapComplexInfo = (props) => {
  const complex = props.selectedComplex.detail.complex;
  const [complexAreaPriceList, setComplexAreaPriceList] = useState([]);

  useEffect(() => {
    searchComplexAreaV4({ complexId: complex.id }, (res) => {
      setComplexAreaPriceList(res.content);
    });
  }, [complex]);

  const onClickMakeLandmark = (e) => {
    const newComplex = { ...props.selectedComplex };
    newComplex.detail.complex.landmark = true;
    updateComplexLandmarkV3({ id: complex.id, landmark: newComplex.detail.complex.landmark }, () => {
      props.onChangeSelectedComplex(newComplex);
      window.Toast.info("LANDMARKED!");
    });
  };

  const onClickCancelLandmark = (e) => {
    const newComplex = { ...props.selectedComplex };
    newComplex.detail.complex.landmark = false;
    updateComplexLandmarkV3({ id: complex.id, landmark: newComplex.detail.complex.landmark }, () => {
      props.onChangeSelectedComplex(newComplex);
      window.Toast.warning("LANDMARK CANCELED!");
    });
  };

  const onClickMakeBookmark = (e) => {
    const newComplex = { ...props.selectedComplex };
    newComplex.detail.complex.bookmark = true;
    updateComplexBookmarkV3({ id: complex.id, bookmark: newComplex.detail.complex.bookmark }, () => {
      props.onChangeSelectedComplex(newComplex);
      window.Toast.info("BOOKMARKED!");
    });
  };

  const onClickCancelBookmark = (e) => {
    const newComplex = { ...props.selectedComplex };
    newComplex.detail.complex.bookmark = false;
    updateComplexBookmarkV3({ id: complex.id, bookmark: newComplex.detail.complex.bookmark }, () => {
      props.onChangeSelectedComplex(newComplex);
      window.Toast.warning("BOOKMARK CANCELED!");
    });
  };

  const onClickClose = () => {
    const { element } = props.selectedComplex;
    element.className = element.className.replace(" active", "");
    props.onChangeSelectedComplex(null);
  };

  const isLandmark = (complex.landmark === true);
  const isBookmark = (complex.bookmark === true);

  return (
    <div style={{ display: "inline-block", background: "#fff", border: "1px solid #000", minWidth: 280, padding: 10 }}>
      <div className="text-left" style={{ fontSize: "1.2em" }}>
        <Link to={`/realestate/complex/${complex.id}`}>{complex.name}</Link>
      </div>
      <div className="text-left" style={{ fontSize: "0.9em", color: "#555" }}>
        <Link to={`/realestate/frontyard/${complex.frontYardId}`}>{complex.frontYardName}</Link> - <Link to={`/realestate/livingarea/${complex.livingAreaId}`}>{complex.livingAreaName}</Link>
      </div>
      <div className={"row no-gutters"} style={{ paddingTop: 5 }}>
        <div className={"col-6 text-left"}>
          {isLandmark && (<button className="btn btn-sm btn-primary" onClick={onClickCancelLandmark}><i className="bi bi-bookmark-star"></i>랜드</button>)}
          {!isLandmark && (<button className="btn btn-sm btn-light" onClick={onClickMakeLandmark}><i className="bi bi-bookmark-star"></i>랜드</button>)}
          &nbsp;
          {isBookmark && (<button className="btn btn-sm btn-info" onClick={onClickCancelBookmark}><i className="bi bi-bookmark-check"></i>북맠</button>)}
          {!isBookmark && (<button className="btn btn-sm btn-light" onClick={onClickMakeBookmark}><i className="bi bi-bookmark-check"></i>북맠</button>)}
        </div>
        <div className={"col-6 text-right"}>
          <button className={"btn btn-sm btn-light"} onClick={onClickClose}>닫기</button>
        </div>
      </div>
      <br />

      <div>
        <ComplexInfroPriceListV2 complexAreaPrices={complexAreaPriceList} />
      </div>
    </div>
  );
};

const ComplexInfroPriceListV2 = ({ complexAreaPrices }) => {

  function _formatDate(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  }

  const timestamps = complexAreaPrices.filter(v => v.updatedDateTime).map(v => new Date(v.updatedDateTime).getTime()).sort((v1, v2) => v2 - v1);

  return (<>
    <table className="fluid">
      <thead></thead>
      <tbody>
        {complexAreaPrices.map((v, i) => (<ComplexInfroPriceListV2Item key={i} item={v} />))}
      </tbody>
    </table>
    {timestamps.length && (<div style={{ paddingTop: 10, color: "#555", fontSize: "0.9em" }}>{_formatDate(new Date(timestamps[0]))}</div>)}
  </>);
};

const ComplexInfroPriceListV2Item = ({ item }) => {
  const tdStyle = { width: "25%", lineHeight: 1.0, textAlign: "center", fontSize: "0.9em" };
  const _lease = (item.leasePrice > 0 && item.lastLeasePrice > 0) ? Math.min(item.leasePrice, item.lastLeasePrice) : (item.leasePrice === 0 && item.lastLeasePrice === 0) ? 0 : Math.max(item.leasePrice, item.lastLeasePrice);
  const _deal = item.dealPrice > 0 ? item.dealPrice : 0;
  const _gap = (_deal > 0 && _lease > 0) ? _deal - _lease : 0;
  return (<>
    <tr>
      <td style={{ ...tdStyle, paddingTop: 8 }} colSpan={4} className="text-left">{item.supplyPyeong}평({item.generalExclusiveArea})</td>
    </tr>
    <tr>
      <td style={{ ...tdStyle }} >매 {(item.dealPrice / 10000).toFixed(1)}</td>
      <td style={{ ...tdStyle }} >전 {(item.leasePrice / 10000).toFixed(1)}</td>
      <td style={{ ...tdStyle }} >매 {item.dealCount}개</td>
      <td style={{ ...tdStyle }} >전 {item.leaseCount}개</td>
    </tr>
    <tr>
      <td style={{ ...tdStyle }}>매 {(item.lastDealPrice / 10000).toFixed(1)}</td>
      <td style={{ ...tdStyle }}>전 {(item.lastLeasePrice / 10000).toFixed(1)}</td>
      <td style={{ ...tdStyle, color: "orangered" }}>갭 {_gap > 0 ? (_gap / 10000).toFixed(1) : 0}</td>
      <td style={{ ...tdStyle, color: "blue" }}>고 {(item.topDealPrice / 10000).toFixed(1)}</td>
    </tr>
  </>);
};

export { ComplexAreaMap };
